/**
 * Phase 3H Part B — Client Profile → Agreements tab.
 *
 * Reads GET /api/hq/control/agreements?business_id=... and lists every
 * commercial agreement issued/accepted for this client. Nothing is
 * fabricated — legacy clients with no agreements simply show an empty
 * state.
 */
import React, { useEffect, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { ExternalLink, GitBranch } from "lucide-react";
import CreateChangeOrderModal from "@/hq/clients/CreateChangeOrderModal";

const CUR_SYM = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (m, c) => `${CUR_SYM[c] || c + " "}${(Number(m || 0) / 100).toFixed(2)}`;
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

const STATUS_TONE = {
  ISSUED:    "bg-[#FFF7EE] text-[#8A4A00] border-[#FCE1C7]",
  VIEWED:    "bg-[#FFF7EE] text-[#8A4A00] border-[#FCE1C7]",
  SCROLLED:  "bg-[#FFF7EE] text-[#8A4A00] border-[#FCE1C7]",
  ACCEPTED:  "bg-[#E7F7EE] text-[#0FA958] border-[#B7E4C7]",
  SUPERSEDED:"bg-[#F1F1F1] text-[#8A8A8A] border-[#EAEAEA]",
  CANCELLED: "bg-[#FDEAEA] text-[#C1350B] border-[#F5CAC3]",
};

export default function ClientAgreementsTab({ businessId }) {
  const [rows, setRows] = useState(null);
  const [err, setErr]   = useState("");
  const [coFor, setCoFor] = useState(null);   // { agreement, invoice }

  const load = () => {
    hqApi.get(`/control/agreements`, { params: { business_id: businessId } })
      .then((r) => setRows(r.data.items || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  };
  useEffect(() => { load(); }, [businessId]);

  const openChangeOrder = async (a) => {
    // Fetch the invoice this agreement governs so the modal can pre-fill
    // paid-so-far + current unpaid schedule accurately.
    if (!a.invoice_id) return;
    try {
      const inv = (await hqApi.get(`/invoices/${a.invoice_id}`)).data;
      setCoFor({ agreement: a, invoice: inv });
    } catch (e) {
      setErr("Could not load invoice for change order.");
    }
  };

  if (err) return <Card className="p-6"><div className="text-sm text-[#C1350B]" data-testid="client-agreements-error">{err}</div></Card>;
  if (rows === null) return <div className="space-y-3"><Skeleton className="h-24"/><Skeleton className="h-24"/></div>;
  if (rows.length === 0) return <EmptyState title="No agreements yet" description="Commercial agreements issued for this client will appear here." testId="client-agreements-empty"/>;

  return (
    <div className="space-y-3" data-testid="client-agreements-tab">
      {rows.map((a) => {
        const isCO = !!a.change_order;
        return (
          <Card key={a.id} className="p-5" data-testid={`client-agreement-row-${a.id}`}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">
                    {a.agreement_type} · {a.territory}
                  </div>
                  <span className={`text-[9px] tracking-[0.14em] uppercase font-black px-2 py-0.5 rounded-full border ${STATUS_TONE[a.status] || STATUS_TONE.ISSUED}`}
                        data-testid={`client-agreement-status-${a.id}`}>
                    {a.status}
                  </span>
                  {isCO && (
                    <span className="text-[9px] tracking-[0.14em] uppercase font-black px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#3A4BD3] border border-[#C6D0F6]">
                      Change Order
                    </span>
                  )}
                </div>
                <div className="mt-1 font-black text-base" data-testid={`client-agreement-ref-${a.id}`}>
                  {a.agreement_reference} <span className="text-[#8A8A8A] font-normal">· v{a.template_version}</span>
                </div>
                <div className="mt-0.5 text-sm text-[#4A4A4A] capitalize">
                  {(a.service_type || "").replace(/_/g, " ")}
                  {(a.project_snapshot?.title) ? ` · ${a.project_snapshot.title}` : ""}
                </div>
                {isCO && a.supersedes_agreement_reference && (
                  <div className="mt-1 text-[11px] text-[#3A4BD3]">
                    Supersedes {a.supersedes_agreement_reference}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">Total value</div>
                <div className="font-black text-lg">
                  {money(a.project_snapshot?.project_value_minor ?? a.commercial_snapshot?.amount_minor, a.currency)}
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <Cell label="Issued" value={fmt(a.issued_at)}/>
              <Cell label="Accepted" value={fmt(a.accepted_at_actual || a.accepted_at)}/>
              <Cell label="Currency" value={a.currency}/>
              <Cell label="Schedule" value={a.payment_schedule?.kind || "—"}/>
            </div>
            {a.payment_schedule?.instalments?.length ? (
              <div className="mt-3 rounded-lg border border-[#EAEAEA] p-3 bg-[#FDFCF9]">
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-2">Payment schedule</div>
                <div className="space-y-1">
                  {a.payment_schedule.instalments.map((i) => (
                    <div key={i.seq} className="flex items-center justify-between text-[12px]">
                      <span>Payment {i.seq} of {a.payment_schedule.instalments.length} — {i.label}</span>
                      <span className="font-black tabular-nums">{money(i.amount_minor, a.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {/* Change Order button — only for ACCEPTED PROJECT agreements
                that govern an invoice. Never shown for subscriptions. */}
            {a.status === "ACCEPTED" && a.agreement_type === "PROJECT" && a.invoice_id && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  data-testid={`client-agreement-change-order-${a.id}`}
                  onClick={() => openChangeOrder(a)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase bg-white border border-[#EAEAEA] hover:border-[#FF6A00] hover:text-[#FF6A00]"
                >
                  <GitBranch size={11}/> Create Change Order
                </button>
              </div>
            )}
          </Card>
        );
      })}

      {coFor && (
        <CreateChangeOrderModal
          open={!!coFor}
          agreement={coFor.agreement}
          invoice={coFor.invoice}
          onClose={() => setCoFor(null)}
          onCreated={() => { load(); }}
        />
      )}
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className="font-bold">{value}</div>
    </div>
  );
}
